import type { SalaryCurrency as SalaryCurrencyType } from '@seek/salary-data';
import { useTranslations } from '@vocab/react';
import { Dropdown } from 'braid-design-system';
import React, { type ChangeEvent, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { EVENT_TYPE_CURRENCY_TYPE_SELECTED } from 'src/app/analytics';
import { actionUpdateSalaryCurrency } from 'src/app/state/modules/formData/salaryCurrency';
import { actionUpdateCajaSalaryCurrency } from 'src/app/state/modules/tealium/cajaSalaryCurrency';
import { useSalaryCurrencyTaxonomy } from 'src/app/taxonomies/useSalaryCurrencyTaxonomy';
import { sendLinkEvent } from 'src/app/utils/analytics';
import type AppState from 'src/types/appState';

import translations from './.vocab';

export const SalaryCurrency = ({
  currency,
}: {
  currency: SalaryCurrencyType;
}) => {
  const previousCurrency = useRef<string>(currency);

  const { tealium } = useSelector(
    (state: AppState) => ({
      tealium: state.tealium,
    }),
    shallowEqual,
  );

  const { t } = useTranslations(translations);

  const dispatch = useDispatch();
  const { currencyOptions } = useSalaryCurrencyTaxonomy(
    currency as SalaryCurrencyType,
  );

  const handleSalaryCurrencyOnChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newCurrency = e.target.value;
    dispatch(actionUpdateSalaryCurrency(newCurrency as SalaryCurrencyType));
    dispatch(actionUpdateCajaSalaryCurrency(newCurrency));
    sendLinkEvent(tealium, {
      eventType: EVENT_TYPE_CURRENCY_TYPE_SELECTED,
      eventName: EVENT_TYPE_CURRENCY_TYPE_SELECTED,
      previousCurrency: previousCurrency.current,
      cajaSalaryCurrency: newCurrency,
    });
  };

  return (
    <Dropdown
      id="salary_currency"
      aria-label={t('salary-currency-aria-label')}
      onChange={handleSalaryCurrencyOnChange}
      value={currency}
      placeholder={t('salary-currency-placeholder')}
      label={t('salary-currency-label')}
      reserveMessageSpace
    >
      {currencyOptions.map(({ text, value }) => (
        <option key={value} value={value}>
          {text}
        </option>
      ))}
    </Dropdown>
  );
};
